/**
 * Router-shaped entry points for the two SSR'd /discover guide routes.
 *
 * These exist so the marketing routes do not go through `DiscoverPage`. That component is the
 * app's dispatcher: it reads `match.params` and picks a guide page, but it also statically
 * imports the legacy browse shell — `DiscoverMap` (mapbox-gl), `BrowsePanel`, `ProfileSidebar`,
 * `TripBuilderBar` — none of which a guide page renders. Routing through it put all of that in
 * the marketing entry bundle that every homepage and blog visitor downloads.
 *
 * The marketing manifest already has a separate spec per URL shape, so there is nothing left
 * for a dispatcher to decide: each route names the page it wants.
 *
 * `initialData` is the SSR loader's payload (marketingSsrLoaders.ts → loadDiscoverGuide) and is
 * absent on client-side navigation, where the pages fall back to their own query.
 */

import DiscoverCountryPage from "./DiscoverCountryPage";
import DiscoverDestinationPage from "./DiscoverDestinationPage";

interface GuideRouteProps {
	match: { params: { country?: string; destination?: string } };
	initialData?: { guide?: any };
	/**
	 * The page's locale, derived from the pathname by `MarketingShell.tsx:69` and handed to every
	 * route component. It must be forwarded: these wrappers destructure their props, so a value
	 * not named here is dropped, and the pages below cannot recover it — `i18n.language` is not a
	 * substitute, because the marketing bundle has no i18next instance during SSR.
	 *
	 * Dropping it is what made /it/discover/japan serve Italian prose under "Weather by month"
	 * and "Who Japan is for": the guide text comes pre-localised from the SSR loader, so only the
	 * chrome — the part that reads the UI dictionary — fell back to English, and it did so in a
	 * way that looks like unfinished translation rather than a broken prop.
	 */
	lang?: string;
}

export const DiscoverCountryRoute = ({ match, initialData, lang }: GuideRouteProps) => (
	<DiscoverCountryPage countrySlug={match.params.country} initialGuide={initialData?.guide} lang={lang} />
);

export const DiscoverDestinationRoute = ({ match, initialData, lang }: GuideRouteProps) => (
	<DiscoverDestinationPage
		countrySlug={match.params.country || ""}
		destinationSlug={match.params.destination || ""}
		initialGuide={initialData?.guide}
		lang={lang}
	/>
);
